import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Download, Upload, Mail, Banknote } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { StatusSelect } from "../components/StatusSelect";
import { Leaderboard, ResidualAlerts, MonthComparison } from "../components/Insights";
import { ForecastPanel } from "../components/Forecast";
import { NegativeMerchants, OwesBadge } from "../components/Carry";
import { TopMerchants } from "../components/TopMerchants";
import { RangeReport } from "../components/RangeReport";
import { AttritionReport } from "../components/Attrition";
import { SaasFeeCard } from "../components/SaasFees";
import { CompanyCards, CompanyBars, CompanySwitch, AgentCompanyBars } from "../components/Companies";
import { money, fee, pct, monthLabel, downloadCSV } from "../lib/format";
import { PageHeader, StatCard, MonthSelect, TrendChart, Panel, Empty, CardBreakdown } from "../components/Widgets";
import { Button } from "../components/ui/button";

export default function AdminDashboard() {
  const [month, setMonth] = useState(null);
  const [company, setCompany] = useState("");
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const reload = () => setTick((x) => x + 1);

  useEffect(() => {
    let live = true;
    api.get("/dashboard", { params: { ...(month ? { month } : {}), ...(company ? { company } : {}) } }).then((r) => live && setD(r.data));
    return () => { live = false; };
  }, [month, company, tick]);

  if (!d) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;
  const t = d.totals;

  const sendEmails = async (agent) => {
    const who = agent ? agent.name : "every agent with a login";
    if (!window.confirm(`Email the ${monthLabel(d.month)} statement to ${who}?`)) return;
    try {
      const { data } = await api.post("/emails/statements", null, { params: { month: d.month, ...(agent ? { agent_id: agent.agent_id } : {}) } });
      if (data.sent.length) toast.success(`Emailed: ${data.sent.join(", ")}`);
      if (data.failed.length) toast.error(`Failed: ${data.failed.map((f) => `${f.name} (${f.error})`).join(", ")}`);
      if (!data.sent.length && !data.failed.length) toast.info("No agents with a login had residuals this month");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = () => downloadCSV(`agent-payouts-${d.month}.csv`,
    ["Agent", "Code", "Email", "Split %", "Merchants", "New accounts", "Volume", "Residual", "Fees", "Net", "Payout"],
    d.agents.map((a) => [a.name, a.agent_code, a.email || "", a.effective_split, a.merchants, a.new_accounts, a.volume, a.residual, a.fee, a.net, a.payout]));
  const exportBatch = () => downloadCSV(`payout-batch-${d.month}.csv`,
    ["Payee", "Agent code", "Email", "Month", "Merchants", "Amount", "Status", "Paid date"],
    d.agents.filter((a) => a.payout > 0).map((a) => [a.name, a.agent_code, a.email || "", d.month, a.merchants, a.payout.toFixed(2), a.status, a.paid_at ? a.paid_at.slice(0, 10) : ""]));

  return (
    <div>
      <PageHeader eyebrow={company ? `Admin overview · ${company} only` : "Admin overview"} title={d.month ? monthLabel(d.month) : "Dashboard"}>
        <CompanySwitch value={company} onChange={setCompany} names={d.company_names} testId="dashboard-company-switch" />
        <MonthSelect months={d.months} value={d.month} onChange={setMonth} />
        {d.month && <Button data-testid="email-statements-button" variant="outline" onClick={() => sendEmails(null)}><Mail size={14} className="mr-2" />Email statements</Button>}
        {d.month && <Button data-testid="payout-batch-button" variant="outline" onClick={exportBatch}><Banknote size={14} className="mr-2" />Payout batch</Button>}
        {d.month && <Button data-testid="export-report-button" variant="outline" onClick={exportCsv}><Download size={14} className="mr-2" />Export</Button>}
        <Button asChild className="bg-blue-500 hover:bg-blue-600 text-white"><Link to="/admin/upload" data-testid="dashboard-upload-button"><Upload size={14} className="mr-2" />Upload report</Link></Button>
      </PageHeader>

      {!d.month ? (
        <Panel><Empty>No reports yet. <Link to="/admin/upload" className="text-blue-400 underline">Upload your first monthly sheet</Link> to see residuals.</Empty></Panel>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
            <StatCard label="Total residual" value={money(t.residual)} testId="total-residual-stat" />
            <StatCard label="Agent payouts" value={money(t.payout)} accent="emerald" testId="total-payout-stat" delay={60} />
            <StatCard label="House residual" value={money(t.house_residual)} hint="house codes · no split" testId="house-net-stat" delay={120} />
            <StatCard label="Volume" value={money(t.volume)} hint={`${t.merchants} merchants · ${money(t.fees)} fees`} testId="total-volume-stat" delay={180} />
            <StatCard label="Unassigned" value={money(t.unassigned_residual)} accent={t.unassigned_residual ? "amber" : undefined} testId="unassigned-stat" delay={240} />
          </div>
          <CompanyCards d={d} payoutLabel="Agents earned" paid={company ? undefined : t.payout} />

          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <Panel title="Month over month" className="lg:col-span-2">
              <div className="p-4"><TrendChart data={d.trend} keys={[{ key: "residual", label: "Residual", color: "#3B82F6" }, { key: "payout", label: "Payouts", color: "#10B981" }]} /></div>
            </Panel>
            <Panel title="Volume by card type">
              <CardBreakdown cards={t.cards} />
            </Panel>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <CompanyBars d={d} title="Residual by company" testId="dashboard-company-bars" />
            <CompanyBars d={d} metric="volume" title="Monthly volume by company" testId="dashboard-company-volume" />
          </div>
          <div className="mb-6"><AgentCompanyBars agents={d.agents} names={d.company_names} /></div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Panel title="House accounts (no split)">
              {d.house.length === 0 ? <Empty>No house-account rows this month.</Empty> : (
                <table className="tbl w-full" data-testid="house-table">
                  <thead><tr><th>Code</th><th className="!text-right">Merchants</th><th className="!text-right">Volume</th><th className="!text-right">Residual</th></tr></thead>
                  <tbody>{d.house.map((h) => (
                    <tr key={h.code}><td className="font-mono">{h.code}</td><td className="text-right font-mono">{h.merchants}</td>
                      <td className="text-right font-mono text-slate-400">{money(h.volume)}</td><td className="text-right font-mono">{money(h.residual)}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </Panel>
            <Panel title="Unmatched agents">
              {d.unassigned.length === 0 ? <Empty>All rows matched to an agent.</Empty> : (
                <div className="divide-y divide-slate-800" data-testid="unassigned-list">
                  {d.unassigned.map((u) => (
                    <div key={u.agent} className="px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm"><AlertTriangle size={14} className="text-amber-400" /><span className="font-mono">{u.agent}</span><span className="text-xs text-slate-500">{u.rows} rows</span></div>
                      <span className="font-mono text-sm text-amber-300">{money(u.residual)}</span>
                    </div>
                  ))}
                  <div className="px-5 py-3 text-xs text-slate-500">Create an agent with a matching code to assign these automatically.</div>
                </div>
              )}
            </Panel>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mb-6">
            <Leaderboard agents={d.agents} />
            <ResidualAlerts month={d.month} />
          </div>

          <NegativeMerchants month={d.month} />
          <TopMerchants />
          <SaasFeeCard className="mb-6" company={company} month={d.month} />
          <RangeReport className="mb-6" />
          <AttritionReport compact className="mb-6" company={company} />
          <MonthComparison d={d} />
          <ForecastPanel />

          <Panel title="Agent breakdown">
            {d.agents.length === 0 ? <Empty>No agents yet. <Link to="/admin/agents" className="text-blue-400 underline">Add agents</Link>.</Empty> : (
              <div className="overflow-x-auto">
                <table className="tbl w-full" data-testid="agent-breakdown-table">
                  <thead><tr><th>Agent</th><th>Code</th><th className="!text-right">Split</th><th className="!text-right">Merchants</th><th className="!text-right">New</th><th className="!text-right">Volume</th><th className="!text-right">Residual</th><th className="!text-right">Fees</th><th className="!text-right">Payout</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {d.agents.filter((a) => !company || a.residual || a.payout || a.merchants).map((a) => (
                      <tr key={a.agent_id} data-testid={`agent-row-${a.agent_code}`}>
                        <td><Link to={`/admin/agents/${a.agent_id}`} className="text-slate-100 hover:text-blue-300">{a.name}</Link><OwesBadge amount={a.carry_out} testId={`owes-badge-${a.agent_code}`} /></td>
                        <td><span className="bg-slate-800 text-blue-400 border border-blue-500/30 font-mono text-xs px-2 py-0.5 rounded">{a.agent_code}</span></td>
                        <td className="text-right font-mono">{pct(a.effective_split)}{a.bonus_tier && <span className="ml-1 text-[10px] text-emerald-400">BONUS</span>}</td>
                        <td className="text-right font-mono">{a.merchants}</td>
                        <td className="text-right font-mono text-slate-400">{a.new_accounts}</td>
                        <td className="text-right font-mono text-slate-400">{money(a.volume)}</td>
                        <td className="text-right font-mono">{money(a.residual)}</td>
                        <td className="text-right font-mono text-rose-300/80">{fee(a.fee)}</td>
                        <td className="text-right font-mono text-emerald-400 font-semibold">{money(a.payout)}</td>
                        <td><StatusSelect month={d.month} agent={a} onChange={reload} /></td>
                        <td className="text-right whitespace-nowrap">
                          <button data-testid={`email-agent-${a.agent_code}`} disabled={!a.has_login || !a.merchants} onClick={() => sendEmails(a)}
                            title={a.has_login ? (a.emailed_at ? `Emailed ${new Date(a.emailed_at).toLocaleString()}` : "Email statement") : "No login email"}
                            className={`transition-colors disabled:opacity-30 ${a.emailed_at ? "text-emerald-400" : "text-slate-500 hover:text-blue-300"}`}><Mail size={15} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
